// Guest cart persisted to localStorage. Logged-in customers use the backend
// cart (cartApi) instead. Only public display data is stored here — never
// anything sensitive. Emits CART_EVENT so the header count updates instantly.
const KEY = 'itec_cart';
export const CART_EVENT = 'cart:changed';

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    // Tolerate the older { items: [...] } shape.
    if (Array.isArray(parsed)) return parsed;
    if (Array.isArray(parsed?.items)) return parsed.items;
    return [];
  } catch {
    return [];
  }
}

function write(list) {
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
    window.dispatchEvent(new CustomEvent(CART_EVENT));
  } catch {
    // ignore quota / serialization errors
  }
}

const pick = (p, camel, snake) => (p?.[camel] !== undefined ? p[camel] : p?.[snake]);

// Snapshot only the public fields the cart UI needs.
function toStored(product, quantity) {
  const brandName = product.brand?.name ?? product.brand_name ?? null;
  const brandSlug = product.brand?.slug ?? product.brand_slug ?? null;
  return {
    productId: product.id ?? product.productId,
    slug: product.slug,
    name: product.name,
    sku: product.sku ?? null,
    model: product.model ?? null,
    brand: brandName ? { name: brandName, slug: brandSlug } : null,
    mainImage: product.image ?? product.main_image_url ?? product.mainImage ?? null,
    regularPrice: pick(product, 'regularPrice', 'regular_price') ?? null,
    salePrice: pick(product, 'salePrice', 'sale_price') ?? null,
    quantity: Math.max(1, Number(quantity) || 1),
    stockStatus: pick(product, 'stockStatus', 'stock_status') ?? null,
    priceVisibility: pick(product, 'priceVisibility', 'price_visibility') ?? null,
    enableCart: !!pick(product, 'enableCart', 'enable_cart'),
    enableInquiry: !!pick(product, 'enableInquiry', 'enable_inquiry'),
  };
}

export function getCart() {
  return read();
}

export function addToCart(product, quantity = 1) {
  const id = product?.id ?? product?.productId;
  if (!id) return read();
  const list = read();
  const existing = list.find((i) => i.productId === id);
  let next;
  if (existing) {
    next = list.map((i) =>
      i.productId === id ? { ...i, quantity: i.quantity + Math.max(1, Number(quantity) || 1) } : i
    );
  } else {
    next = [...list, toStored(product, quantity)];
  }
  write(next);
  return next;
}

export function updateCartItem(productId, quantity) {
  const qty = Math.max(1, Number(quantity) || 1);
  const next = read().map((i) => (i.productId === productId ? { ...i, quantity: qty } : i));
  write(next);
  return next;
}

export function removeFromCart(productId) {
  const next = read().filter((i) => i.productId !== productId);
  write(next);
  return next;
}

export function clearCart() {
  write([]);
}

export function getCartCount() {
  return read().reduce((n, i) => n + (Number(i.quantity) || 0), 0);
}

export function getCartTotal() {
  return read().reduce((sum, i) => {
    const unit = Number(i.salePrice) > 0 ? Number(i.salePrice) : Number(i.regularPrice) || 0;
    return sum + unit * (Number(i.quantity) || 0);
  }, 0);
}

// Returns the guest cart as a sync payload ([{ productId, quantity }]). The
// caller pushes it to the backend then calls clearCart() on success.
export function syncCartAfterLogin() {
  return read().map((i) => ({ productId: i.productId, quantity: i.quantity }));
}
